"use client"

import {
  navigateWithThreadPreservation,
  useCVDocuments,
  useArtifactCanvas,
  DashboardMetricCard,
  DashboardMetricGrid,
  DashboardSectionAction,
  DashboardSectionTitle,
  DashboardSectionSkeleton,
  DashboardStatsSkeleton,
  PaneResponsiveCardGrid,
  sortBriefsForDashboardPreview,
  BRIEFS_CREATE_HREF,
  talentDisplayName,
} from '@yeshuman/sdk'
import { useCallback, useMemo, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { FileText, FilePen, Search, Upload, UserCheck } from 'lucide-react'
import { useBriefs } from '../../hooks/use-briefs'
import { BriefListCard } from './brief-list-card'
import { TalentProfileCard } from './talent-profile-card'
import { TalentProfilePreviewCanvas } from './talent-profile-preview-canvas'

function countLabel(count: number, singular: string, plural: string): string {
  return count === 1 ? singular : plural
}

export function RecruiterDashboardSections() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { briefs = [], isLoading: briefsLoading, error: briefsError } = useBriefs()
  const { cvDocuments, isLoading: cvsLoading, error: cvsError } = useCVDocuments()
  const { openCanvas, closeCanvas } = useArtifactCanvas()
  const previewOpenKeyRef = useRef(0)

  const activeCount = briefs.filter((b) => b.status === 'active').length
  const draftCount = briefs.filter((b) => b.status === 'draft').length
  const savedCandidatesTotal = briefs.reduce((sum, b) => sum + b.saved_candidates_count, 0)
  const parsedCount = cvDocuments.filter((cv) => cv.parsing_status === 'completed').length
  const processingCount = cvDocuments.filter(
    (cv) => cv.parsing_status === 'pending' || cv.parsing_status === 'processing',
  ).length
  const failedCount = cvDocuments.filter((cv) => cv.parsing_status === 'failed').length

  const previewBriefs = useMemo(() => sortBriefsForDashboardPreview(briefs, 4), [briefs])
  const recentTalentPreview = useMemo(
    () =>
      [...cvDocuments]
        .sort((a, b) => new Date(b.uploaded_at).getTime() - new Date(a.uploaded_at).getTime())
        .slice(0, 4),
    [cvDocuments],
  )

  const openProfilePreview = useCallback(
    (document: (typeof cvDocuments)[number]) => {
      const profileId = document.parsed_profile_id
      if (!profileId) return

      previewOpenKeyRef.current += 1
      const displayName = talentDisplayName(document)
      openCanvas({
        title: displayName,
        preventAutoFocus: true,
        content: (
          <TalentProfilePreviewCanvas
            key={`dashboard-talent-${profileId}-${previewOpenKeyRef.current}`}
            profileId={profileId}
            profileName={displayName}
            onClose={closeCanvas}
          />
        ),
      })
    },
    [closeCanvas, openCanvas],
  )

  return (
    <div className="space-y-6">
      {briefsLoading ? (
        <DashboardStatsSkeleton count={4} maxColumns={4} />
      ) : briefsError ? (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-6 text-center text-sm">
          <p className="font-medium text-destructive">Could not load briefs</p>
          <p className="mt-1 text-muted-foreground">{(briefsError as Error).message ?? 'Something went wrong.'}</p>
        </div>
      ) : (
        <DashboardMetricGrid maxColumns={4}>
          <DashboardMetricCard title="Active briefs" value={activeCount} icon={FileText} />
          <DashboardMetricCard title="Draft briefs" value={draftCount} icon={FilePen} />
          <DashboardMetricCard
            title={countLabel(savedCandidatesTotal, 'Saved candidate', 'Saved candidates')}
            value={savedCandidatesTotal}
            icon={UserCheck}
          />
          <DashboardMetricCard
            title={countLabel(cvDocuments.length, 'In talent', 'In talent')}
            value={cvsLoading ? '—' : cvDocuments.length}
            description={cvDocuments.length > 0 ? 'Parsed profiles' : undefined}
            icon={Upload}
            loading={cvsLoading && cvDocuments.length === 0}
          />
        </DashboardMetricGrid>
      )}

      {briefsLoading ? (
        <DashboardSectionSkeleton cards={4} grid />
      ) : (
        <section className="space-y-4" aria-labelledby="recent-briefs-heading">
          <DashboardSectionTitle
            id="recent-briefs-heading"
            title="Recent briefs"
            action={
              <DashboardSectionAction
                onClick={() => navigateWithThreadPreservation(navigate, '/recruiter/briefs', searchParams)}
              >
                View all
              </DashboardSectionAction>
            }
          />

          {previewBriefs.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-10 text-center">
              <FileText className="mb-3 h-10 w-10 text-muted-foreground" aria-hidden />
              <p className="text-sm font-medium">No briefs yet</p>
              <p className="mt-1 max-w-sm text-sm text-muted-foreground">
                Create a brief to organize candidate searches, track saved candidates, and manage your pipeline.
              </p>
              <div className="mt-4">
                <DashboardSectionAction
                  onClick={() =>
                    navigateWithThreadPreservation(navigate, BRIEFS_CREATE_HREF, searchParams)
                  }
                >
                  Create brief
                </DashboardSectionAction>
              </div>
            </div>
          ) : (
            <PaneResponsiveCardGrid>
              {previewBriefs.map((brief) => (
                <BriefListCard
                  key={brief.id}
                  brief={brief}
                  variant="compact"
                  className="h-full"
                  onOpen={() =>
                    navigateWithThreadPreservation(navigate, `/recruiter/briefs/${brief.id}`, searchParams)
                  }
                />
              ))}
            </PaneResponsiveCardGrid>
          )}
        </section>
      )}

      {cvsLoading ? (
        <DashboardSectionSkeleton cards={2} grid />
      ) : (
        <section className="space-y-4" aria-labelledby="talent-heading">
          <DashboardSectionTitle
            id="talent-heading"
            title="Talent"
            action={
              <DashboardSectionAction
                onClick={() => navigateWithThreadPreservation(navigate, '/recruiter/talent', searchParams)}
              >
                View all
              </DashboardSectionAction>
            }
          />

          {cvsError ? (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-6 text-center text-sm">
              <p className="font-medium text-destructive">Could not load talent</p>
              <p className="mt-1 text-muted-foreground">{(cvsError as Error).message ?? 'Something went wrong.'}</p>
            </div>
          ) : cvDocuments.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-10 text-center">
              <Search className="mb-3 h-10 w-10 text-muted-foreground" aria-hidden />
              <p className="text-sm font-medium">No talent yet</p>
              <p className="mt-1 max-w-sm text-sm text-muted-foreground">
                Upload files to parse profiles with skills and experience you can browse and search.
              </p>
              <div className="mt-4">
                <DashboardSectionAction
                  onClick={() => {
                    const next = new URLSearchParams(searchParams)
                    next.set('upload', '1')
                    navigateWithThreadPreservation(navigate, '/recruiter/talent', next)
                  }}
                >
                  Upload files
                </DashboardSectionAction>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {cvDocuments.length} {cvDocuments.length === 1 ? 'profile' : 'profiles'} · {parsedCount} parsed
                {processingCount > 0 ? ` · ${processingCount} processing` : ''}
                {failedCount > 0 ? ` · ${failedCount} failed` : ''}
              </p>
              {recentTalentPreview.length > 0 ? (
                <PaneResponsiveCardGrid>
                  {recentTalentPreview.map((document) => (
                    <TalentProfileCard
                      key={document.id}
                      document={document}
                      variant="hub"
                      className="h-full"
                      onOpen={() => openProfilePreview(document)}
                    />
                  ))}
                </PaneResponsiveCardGrid>
              ) : null}
            </div>
          )}
        </section>
      )}
    </div>
  )
}
